<template>
  <v-card class="py-2">
    <v-icon v-on:click.stop="$emit('close')" large class="closeIcon mt-3 mx-2"
      >mdi-close</v-icon
    >
    <h2
      weight="200"
      class="mt-6 d-flex font-weight-regular justify-center align-center"
    >
      Ajouter un fichier au dossier "{{ parent ? parent.name : "..." }}"
    </h2>
    <v-divider light color="#EF7D00" class="mt-4 mb-6 mx-4"></v-divider>
    <v-form
      ref="form"
      v-model="isFormValid"
      class="d-flex flex-column px-10 py-4"
    >
      <v-select
        label="Type du fichier"
        v-model="type"
        :items="types"
        item-text="label"
        item-value="value"
      ></v-select>
      <!--- PDF Inputs -->
      <div v-if="type === 'pdf'">
        <v-file-input
          v-model="media"
          accept=".pdf"
          show-size
          :label="`Importer le fichier (.pdf) | Max : ${this.maxSize} Mo`"
          :rules="media_rule"
          class="no-truncate"
        ></v-file-input>
        <div v-if="media">
          <v-row>
            <v-text-field
              v-model="name"
              label="Nom du fichier"
              prepend-icon="mdi-file-document"
              class="mt-4 mx-4"
              required
              :rules="name_rule"
            ></v-text-field>
          </v-row>
          <v-row class="d-flex align-center mb-3">
            <v-col>Visibilité : </v-col>
          </v-row>
          <div
            v-for="field in visibilityFields"
            :key="field.key"
            class="ml-4 mb-2"
          >
            <v-autocomplete
              v-model="visibility[field.key]"
              :items="field.items"
              :label="field.label"
              :prepend-icon="field.icon"
              :rules="field.rules"
              multiple
              dense
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  close
                  class="mx-1 my-1"
                  @click:close="removeVisibilityItem(field.key, item)"
                >
                  {{ item }}
                </v-chip>
              </template>
            </v-autocomplete>
          </div>
        </div>
      </div>
      <!--- YOUTUBE VIDEOS Inputs -->
      <div v-else-if="type === 'youtube'">
        <v-text-field
          v-model="link"
          label="Lien de la vidéo"
          prepend-icon="mdi-link"
          placeholder="https://youtu.be/... OU https://www.youtube.com/watch?v=..."
          required
          :rules="link_rule"
        ></v-text-field>
        <v-text-field
          v-model="name"
          label="Nom de la vidéo"
          prepend-icon="mdi-file-document"
          required
          :rules="name_rule"
        ></v-text-field>
        <v-row class="d-flex align-center mb-3">
          <v-col>Visibilité : </v-col>
        </v-row>
        <div
          v-for="field in visibilityFields"
          :key="field.key"
          class="ml-4 mb-2"
        >
          <v-autocomplete
            v-model="visibility[field.key]"
            :items="field.items"
            :label="field.label"
            :prepend-icon="field.icon"
            :rules="field.rules"
            multiple
            dense
          >
            <template v-slot:selection="{ item }">
              <v-chip
                close
                class="mx-1 my-1"
                @click:close="removeVisibilityItem(field.key, item)"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-autocomplete>
        </div>
      </div>
    </v-form>
    <ValidateErrorButtons
      @successClicked="validate"
      @errorClicked="closeDialog"
      :disabled="!isFormValid"
    >
      <template v-slot:error>ANNULER</template>
      <template v-slot:success>VALIDER</template>
    </ValidateErrorButtons>
  </v-card>
</template>

<script>
import ValidateErrorButtons from "../../components/ValidateErrorButtons.vue";

const API_TO_UI_ROLES = { teacher: "Tuteur", student: "Tutoré" };
const UI_TO_API_ROLES = { Tuteur: "teacher", Tutoré: "student" };

export default {
  name: "DialogAddFile",
  components: {
    ValidateErrorButtons,
  },
  props: {
    parent: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        children: [],
        visibility: { roles: [], schools: [], years: [] },
      }),
    },
  },
  data: () => ({
    isFormValid: false,
    media: null,
    maxSize: 20, // en Mo
    type: null,
    types: [
      { label: "PDF", value: "pdf" },
      { label: "Vidéo YouTube", value: "youtube" },
    ],
    name: "",
    link: null,
    visibility: {
      roles: [],
      schools: [],
      years: [],
    },
  }),
  computed: {
    media_rule: function () {
      if (this.type !== "pdf") return [true];
      return [
        !!this.media || "Vous devez importer un pdf",
        this.media?.type === "application/pdf" || "Le fichier doit être un pdf",
        this.media?.size <= this.maxSize * 1024 * 1024 ||
          `Le fichier ne peut pas dépasser ${this.maxSize} Mo`,
      ];
    },
    name_rule: function () {
      return [
        !!this.name || "Le nom ne peut pas être vide",
        !this.parent.children.files
          .map((i) => i.name.toLowerCase())
          .includes(this.name.toLowerCase()) ||
          "ce nom est déjà utilisé par un autre fichier",
      ];
    },
    link_rule: function () {
      if (this.type !== "youtube") return [true];
      return [
        !!this.link || "Le lien de la vidéo ne peut pas être vide",
        /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[&?].*)?$/.test(
          this.link
        ) || "Le lien de la vidéo n'est pas au bon format",
      ];
    },
    visibilityFields() {
      return [
        {
          key: "roles",
          label: "Rôles",
          icon: "mdi-book",
          items: ["Tuteur", "Tutoré"],
          rules: this.createVisibilityRules("roles", "rôle"),
        },
        {
          key: "schools",
          label: "Écoles",
          icon: "mdi-school",
          items: ["ESILV", "EMLV", "IIM"],
          rules: this.createVisibilityRules("schools", "école"),
        },
        {
          key: "years",
          label: "Années",
          icon: "mdi-cake",
          items: ["A1", "A2", "A3", "A4", "A5"],
          rules: this.createVisibilityRules("years", "année"),
        },
      ];
    },
  },
  methods: {
    createVisibilityRules(key, singularName) {
      if (!this.parent?.visibility) return [true];

      let parentVisibilityItems = [];
      if (key === "roles") {
        parentVisibilityItems =
          this.parent.visibility.roles?.map((r) => API_TO_UI_ROLES[r] || r) ||
          [];
      } else {
        parentVisibilityItems = this.parent.visibility[key] || [];
      }

      return [
        (v) => {
          const errors = (v || []).filter(
            (item) => !parentVisibilityItems.includes(item)
          );
          if (errors.length === 0) return true;
          const plural = errors.length > 1;
          return `Le${plural ? "s" : ""} ${singularName}${
            plural ? "s" : ""
          } "${errors.join(", ")}" n'est pas permis par le dossier parent.`;
        },
      ];
    },
    removeVisibilityItem(key, item) {
      const index = this.visibility[key].indexOf(item);
      if (index >= 0) this.visibility[key].splice(index, 1);
    },
    validate: function () {
      this.$refs.form.validate();
      if (!this.isFormValid) return;
      if (!this.parent?._id) {
        console.error("Parent ID is missing, cannot create file.");
        return;
      }

      let data;
      if (this.type === "pdf") {
        data = new FormData();
        data.append("name", this.name);
        data.append("parent", this.parent._id);
        data.append("type", this.type);
        data.append(
          "visibility",
          JSON.stringify({
            roles: this.visibility.roles.map((r) => UI_TO_API_ROLES[r] || r),
            schools: this.visibility.schools,
            years: this.visibility.years,
          })
        );
        data.append("file", this.media); // le fichier PDF
      } else if (this.type === "youtube") {
        data = {
          name: this.name,
          parent: this.parent._id,
          type: this.type,
          link: this.link,
          visibility: JSON.stringify({
            roles: this.visibility.roles.map((r) => UI_TO_API_ROLES[r] || r),
            schools: this.visibility.schools,
            years: this.visibility.years,
          }),
        };
      }
      this.$store
        .dispatch("leodata/createFile", { type: this.type, data: data })
        .then(() => {
          this.closeDialog();
        });
    },
    closeDialog() {
      this.$emit("close");
    },
    initializeForm() {
      this.media = null;
      this.name = "";
      if (this.parent?.visibility) {
        this.visibility.roles =
          this.parent.visibility.roles?.map((r) => API_TO_UI_ROLES[r]) || [];
        this.visibility.schools = [...(this.parent.visibility.schools || [])];
        this.visibility.years = [...(this.parent.visibility.years || [])];
      } else {
        this.visibility = { roles: [], schools: [], years: [] };
      }
    },
  },
  created() {
    this.initializeForm();
  },
  watch: {
    parent: {
      handler(newParent) {
        if (newParent) {
          this.initializeForm();
        }
      },
      deep: true,
      immediate: true,
    },
    media(newFile) {
      if (newFile && newFile.name) {
        const nameWithoutExt = newFile.name.replace(/\.pdf$/i, "");
        this.name = nameWithoutExt;
      }
    },
  },
};
</script>
<style scoped>
.v-sheet {
  position: relative;
  color: #8f8f8f;
}
.v-icon,
.v-input {
  color: #8f8f8f;
}
.closeIcon {
  position: absolute;
  right: 0;
  top: 0;
}

.no-truncate .v-file-input__text {
  color: red;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
